var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as THREE from "../../src/insole-web-common/vendor/three/build/three.module.js";
import { TransformRay } from "../geometry.js";
import { mm2cm } from "../insole-web-common/src/engine/parameters.js";
import { UpdateGeometryFromMeshData } from "../insole-web-common/src/mesh-utils.js";
import { DeepClone } from "../insole-web-common/src/util.js";
import { IsFloat3 } from "../metadata/metadata-hp.js";
import { Vector3ToFloat3 } from "../util.js";
import { ImportFootScanMesh } from "./common.js";
import { GetCurrentMetadataVersion } from "../metadata/metadata-backwards-compatibility.js";
const markerImgSrc = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Cg stroke-linecap='round' stroke-width='.85' stroke='%23000'%3E%3Cpath d='M16 .5v31'/%3E%3Cpath d='M31.5 16H0'/%3E%3C/g%3E%3Ccircle fill='%230dabed' cx='16' cy='16' r='2.5'/%3E%3C/svg%3E";
const footScanSpacing = 0.5;
const halfSpacing = footScanSpacing * 0.5;
const borderPadding = 1;
const defaultCameraViewportSize = 20;
const defaultCameraDistance = 40;
const cameraPosition = new THREE.Vector3(0, -defaultCameraDistance, 0);
const cameraRotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI / 2, -Math.PI / 2, 0, "ZYX"));
const footScanMaterial = new THREE.MeshPhongMaterial();
const tmpMatrix4 = new THREE.Matrix4();
let markerLoaderPromise = null;
export class LandmarkScene {
    static create(visualizer, renderer, initData) {
        return __awaiter(this, void 0, void 0, function* () {
            markerLoaderPromise !== null && markerLoaderPromise !== void 0 ? markerLoaderPromise : (markerLoaderPromise = new Promise((res) => __awaiter(this, void 0, void 0, function* () {
                const textureSize = 128;
                const img = new Image();
                img.src = markerImgSrc;
                yield img.decode();
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                canvas.width = textureSize;
                canvas.height = textureSize;
                ctx.drawImage(img, 0, 0, textureSize, textureSize);
                res(canvas);
            })));
            const initialLandmarks = (() => {
                var _a, _b, _c, _d, _e, _f;
                if (initData.landmarks === null) {
                    return null;
                }
                function GetLandmarkOrNull(landmark) {
                    if (IsFloat3(landmark)) {
                        return landmark;
                    }
                    return null;
                }
                switch (initData.landmarks.type) {
                    case "explicit":
                        return initData.landmarks.positions;
                    case "from-hp-metadata": {
                        const metadata = initData.landmarks.metadata;
                        const leftFootParameters = (_a = metadata.leftFootMetadata) === null || _a === void 0 ? void 0 : _a.footScanParameters;
                        const rightFootParameters = (_b = metadata.leftFootMetadata) === null || _b === void 0 ? void 0 : _b.footScanParameters;
                        return {
                            left: {
                                mpj1: GetLandmarkOrNull(leftFootParameters === null || leftFootParameters === void 0 ? void 0 : leftFootParameters.mpj1),
                                mpj5: GetLandmarkOrNull(leftFootParameters === null || leftFootParameters === void 0 ? void 0 : leftFootParameters.mpj5),
                            },
                            right: {
                                mpj1: GetLandmarkOrNull(rightFootParameters === null || rightFootParameters === void 0 ? void 0 : rightFootParameters.mpj1),
                                mpj5: GetLandmarkOrNull(rightFootParameters === null || rightFootParameters === void 0 ? void 0 : rightFootParameters.mpj5),
                            },
                        };
                    }
                    case "from-leo-metadata": {
                        const metadata = initData.landmarks.metadata;
                        const leftFootParameters = (_d = (_c = metadata.left) === null || _c === void 0 ? void 0 : _c.footScanParameters) === null || _d === void 0 ? void 0 : _d.landmarkPositions;
                        const rightFootParameters = (_f = (_e = metadata.right) === null || _e === void 0 ? void 0 : _e.footScanParameters) === null || _f === void 0 ? void 0 : _f.landmarkPositions;
                        return {
                            left: {
                                mpj1: GetLandmarkOrNull(leftFootParameters === null || leftFootParameters === void 0 ? void 0 : leftFootParameters.mpj1),
                                mpj5: GetLandmarkOrNull(leftFootParameters === null || leftFootParameters === void 0 ? void 0 : leftFootParameters.mpj5),
                            },
                            right: {
                                mpj1: GetLandmarkOrNull(rightFootParameters === null || rightFootParameters === void 0 ? void 0 : rightFootParameters.mpj1),
                                mpj5: GetLandmarkOrNull(rightFootParameters === null || rightFootParameters === void 0 ? void 0 : rightFootParameters.mpj5),
                            },
                        };
                    }
                    default:
                        unreachable(initData.landmarks);
                }
            })();
            function ImportFootScan(data, side) {
                return __awaiter(this, void 0, void 0, function* () {
                    var _a, _b, _c, _d, _e, _f, _g;
                    if (data === null) {
                        return null;
                    }
                    const engine = yield visualizer.acquireEngine(side);
                    yield engine.SetDefaultInsoleParameters();
                    const allTemplateCategories = 1 /* TemplateCategoryMask.HPUS */ |
                        2 /* TemplateCategoryMask.HPEU */ |
                        4 /* TemplateCategoryMask.LeoUS */ |
                        8 /* TemplateCategoryMask.LeoEU */;
                    yield engine.SetEnabledTemplateCategories(allTemplateCategories);
                    const activationResult = yield visualizer.ActivateEngine(engine);
                    if (activationResult !== 0 /* EngineActivationResult.Success */) {
                        return null;
                    }
                    const importResult = yield ImportFootScanMesh(engine, data);
                    if (importResult === null || importResult.meshData === null) {
                        return null;
                    }
                    const [boundingBox, raycaster, insoleTemplates, _alignedPose] = yield Promise.all([
                        engine.MeshCalculateBoundingBox([importResult.ptr]),
                        engine.RaycasterCreate(importResult.ptr),
                        engine.GetTemplates(),
                        engine.SetFootScan({
                            alignWithInsole: false,
                            meshPtr: importResult.ptr,
                            scanType: (_a = data.scanMode) !== null && _a !== void 0 ? _a : 0 /* FootScanType.SemiWeighted */,
                            side,
                        }),
                    ]);
                    const initialLandmarksForCurrentSide = side === 0 /* FootSide.Left */ ? ((_b = initialLandmarks === null || initialLandmarks === void 0 ? void 0 : initialLandmarks.left) !== null && _b !== void 0 ? _b : null) : ((_c = initialLandmarks === null || initialLandmarks === void 0 ? void 0 : initialLandmarks.right) !== null && _c !== void 0 ? _c : null);
                    if (initialLandmarksForCurrentSide !== null) {
                        const landmarksToUpdate = [];
                        if (initialLandmarksForCurrentSide.mpj1 !== null) {
                            landmarksToUpdate.push({
                                name: 0 /* FootscanLandmarkName.Mpj1 */,
                                position: initialLandmarksForCurrentSide.mpj1,
                            });
                        }
                        if (initialLandmarksForCurrentSide.mpj5 !== null) {
                            landmarksToUpdate.push({
                                name: 2 /* FootscanLandmarkName.Mpj5 */,
                                position: initialLandmarksForCurrentSide.mpj5,
                            });
                        }
                        if (landmarksToUpdate.length > 0) {
                            yield engine.SetFootscanLandmark(...landmarksToUpdate);
                        }
                    }
                    const [landmarks, bestFitInsoleData] = yield Promise.all([
                        engine.GetFootscanLandmarks(),
                        engine.SelectBestFitInsole({}),
                    ]);
                    const footScanGeometry = new THREE.BufferGeometry();
                    UpdateGeometryFromMeshData(footScanGeometry, importResult.meshData);
                    const footScanObject = new THREE.Mesh(footScanGeometry, footScanMaterial);
                    footScanObject.frustumCulled = false;
                    visualizer.releaseEngine(side);
                    return {
                        visualizer,
                        raycaster,
                        boundingBox,
                        insoleTemplates,
                        initialBestFitInsole: bestFitInsoleData === null ? null : insoleTemplates[bestFitInsoleData.templateIndex],
                        landmarks: {
                            mpj1: (_e = (_d = initialLandmarksForCurrentSide === null || initialLandmarksForCurrentSide === void 0 ? void 0 : initialLandmarksForCurrentSide.mpj1) !== null && _d !== void 0 ? _d : landmarks === null || landmarks === void 0 ? void 0 : landmarks.mpj1) !== null && _e !== void 0 ? _e : null,
                            mpj5: (_g = (_f = initialLandmarksForCurrentSide === null || initialLandmarksForCurrentSide === void 0 ? void 0 : initialLandmarksForCurrentSide.mpj5) !== null && _f !== void 0 ? _f : landmarks === null || landmarks === void 0 ? void 0 : landmarks.mpj5) !== null && _g !== void 0 ? _g : null,
                        },
                        footScanObject,
                    };
                });
            }
            const [leftData, rightData] = yield Promise.all([
                ImportFootScan(initData.left, 0 /* FootSide.Left */),
                ImportFootScan(initData.right, 1 /* FootSide.Right */),
            ]);
            if (leftData === null && rightData === null) {
                return null;
            }
            return new LandmarkScene(renderer, leftData, rightData, initData.onLandmarkChanged, yield markerLoaderPromise);
        });
    }
    constructor(renderer, leftScan, rightScan, landmarkChangeCallback, markerImage) {
        var _a, _b;
        this.scene = new THREE.Scene();
        this.camera = new THREE.OrthographicCamera();
        this.raycaster = new THREE.Raycaster();
        this.metadata = {};
        this.cameraViewportSize = new THREE.Vector2(defaultCameraViewportSize, defaultCameraViewportSize);
        this.renderRequested = false;
        this.disposed = false;
        this.raycastEventHandler = (ev) => __awaiter(this, void 0, void 0, function* () {
            if (ev.button !== 0) {
                return;
            }
            const canvas = this.renderer.domElement;
            const canvasRect = canvas.getBoundingClientRect();
            const x = ((ev.clientX - canvasRect.left) / canvasRect.width) * 2 - 1;
            const y = -((ev.clientY - canvasRect.top) / canvasRect.height) * 2 + 1;
            this.raycaster.setFromCamera({ x, y }, this.camera);
            const hit = yield this.raycastGlobal(this.raycaster.ray);
            if (hit !== null) {
                // Filter point, decide which landmark it is
                // Only register if the hit is on the top half of the foot scan
                if (hit.point.x < 0) {
                    // Lateral side is mpj5, medial side is mpj1
                    const isLeftFoot = hit.side === 0 /* FootSide.Left */;
                    const isPositiveSide = hit.point.z > 0;
                    const isMPJ5 = isLeftFoot === isPositiveSide;
                    const targetObject = (() => {
                        if (isMPJ5) {
                            return isLeftFoot ? this.leftMpj5Marker : this.rightMpj5Marker;
                        }
                        else {
                            return isLeftFoot ? this.leftMpj1Marker : this.rightMpj1Marker;
                        }
                    })();
                    const hitPoint = Vector3ToFloat3(hit.point);
                    const sideData = this[hit.side === 0 /* FootSide.Left */ ? "leftScan" : "rightScan"];
                    if (sideData !== null) {
                        const { visualizer, insoleTemplates } = sideData;
                        const engine = yield visualizer.acquireEngine(hit.side);
                        const [, selectBestFitInsoleResult] = yield Promise.all([
                            engine.SetFootscanLandmark({
                                name: isMPJ5 ? 2 /* FootscanLandmarkName.Mpj5 */ : 0 /* FootscanLandmarkName.Mpj1 */,
                                position: hitPoint,
                            }),
                            engine.SelectBestFitInsole({}),
                        ]);
                        visualizer.releaseEngine(hit.side);
                        if (selectBestFitInsoleResult !== null) {
                            const selectedTemplate = insoleTemplates[selectBestFitInsoleResult.templateIndex];
                            this.updateMetadata(hit.side, null, selectedTemplate, false); // Callback will be sent with the landmarks
                        }
                    }
                    this.updateMetadata(hit.side, { isMPJ5, position: hitPoint }, null);
                    targetObject.position.copy(hit.point).applyMatrix4(hit.matrix);
                    targetObject.visible = true;
                    this.render();
                }
            }
        });
        this.renderer = renderer;
        this.leftScan = leftScan;
        this.rightScan = rightScan;
        this.landmarkChangeCallback = landmarkChangeCallback;
        if (this.leftScan !== null) {
            this.scene.add(this.leftScan.footScanObject);
        }
        if (this.rightScan !== null) {
            this.scene.add(this.rightScan.footScanObject);
        }
        this.scene.add(new THREE.HemisphereLight(0, 0xf0f0f0));
        this.camera.position.copy(cameraPosition);
        this.camera.quaternion.copy(cameraRotation);
        // Marker data
        this.markerTexture = new THREE.Texture(markerImage);
        this.markerTexture.needsUpdate = true;
        this.markerGeometry = new THREE.PlaneGeometry(1, 1, 1, 1).rotateX(Math.PI / 2);
        this.markerMaterial = new THREE.MeshBasicMaterial({
            map: this.markerTexture,
            transparent: true,
            side: THREE.DoubleSide,
            depthTest: false,
        });
        const createMarker = (name) => {
            const marker = new THREE.Mesh(this.markerGeometry, this.markerMaterial);
            marker.scale.setScalar(2.5);
            marker.renderOrder = 1;
            marker.visible = false;
            marker.name = name;
            this.scene.add(marker);
            return marker;
        };
        this.leftMpj1Marker = createMarker("leftMpj1Marker");
        this.leftMpj5Marker = createMarker("leftMpj5Marker");
        this.rightMpj1Marker = createMarker("rightMpj1Marker");
        this.rightMpj5Marker = createMarker("rightMpj5Marker");
        // Update foot scan positioning
        {
            const tempVec3 = new THREE.Vector3();
            const updatePositioning = ({ boundingBox, footScanObject }, isLeft) => {
                const boundingBoxSizeZ = boundingBox.maxP.z - boundingBox.minP.z;
                const boundingBoxSizeY = boundingBox.maxP.y - boundingBox.minP.y;
                footScanObject.position.set(0, -boundingBoxSizeY * 0.5, (boundingBoxSizeZ * 0.5 + halfSpacing) * (isLeft ? 1 : -1));
            };
            if (this.leftScan !== null) {
                updatePositioning(this.leftScan, true);
            }
            if (this.rightScan !== null) {
                updatePositioning(this.rightScan, false);
            }
            // Calculate the combined bounding box of both foot scans (if available)
            // This will be used for setting the camera position so that both foot scans are visible regardless of the window size
            const combinedBox = new THREE.Box3();
            if (this.leftScan) {
                const bbox = this.leftScan.boundingBox;
                combinedBox.expandByPoint(tempVec3.set(bbox.minP.x, bbox.minP.y, bbox.minP.z).add(this.leftScan.footScanObject.position));
                combinedBox.expandByPoint(tempVec3.set(bbox.maxP.x, bbox.maxP.y, bbox.maxP.z).add(this.leftScan.footScanObject.position));
            }
            if (this.rightScan) {
                const bbox = this.rightScan.boundingBox;
                combinedBox.expandByPoint(tempVec3.set(bbox.minP.x, bbox.minP.y, bbox.minP.z).add(this.rightScan.footScanObject.position));
                combinedBox.expandByPoint(tempVec3.set(bbox.maxP.x, bbox.maxP.y, bbox.maxP.z).add(this.rightScan.footScanObject.position));
            }
            this.cameraViewportSize
                .set(Math.max(Math.abs(combinedBox.min.z), Math.abs(combinedBox.max.z)), Math.max(Math.abs(combinedBox.min.x), Math.abs(combinedBox.max.x)))
                .addScalar(borderPadding);
            const rendererSize = this.renderer.getSize(new THREE.Vector2());
            this.setSize(rendererSize.x, rendererSize.y);
        }
        this.resetLandmarks(false);
        this.updateMetadata(0 /* FootSide.Left */, null, (_a = leftScan === null || leftScan === void 0 ? void 0 : leftScan.initialBestFitInsole) !== null && _a !== void 0 ? _a : null, false);
        this.updateMetadata(1 /* FootSide.Right */, null, (_b = rightScan === null || rightScan === void 0 ? void 0 : rightScan.initialBestFitInsole) !== null && _b !== void 0 ? _b : null, true);
        renderer.domElement.addEventListener("pointerdown", this.raycastEventHandler);
        this.render();
    }
    resetLandmarks(sendCallback = true) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const setInitialLandmarks = (side, isMPJ5, position) => {
            var _a, _b;
            if (position === null) {
                return;
            }
            const isLeftFoot = side === 0 /* FootSide.Left */;
            const marker = (() => {
                if (isMPJ5) {
                    return isLeftFoot ? this.leftMpj5Marker : this.rightMpj5Marker;
                }
                else {
                    return isLeftFoot ? this.leftMpj1Marker : this.rightMpj1Marker;
                }
            })();
            marker.position.set(position.x, position.y, position.z);
            this.updateMetadata(side, { isMPJ5, position }, null, false);
            const footScanObject = (_b = (_a = (side === 0 /* FootSide.Left */ ? this.leftScan : this.rightScan)) === null || _a === void 0 ? void 0 : _a.footScanObject) !== null && _b !== void 0 ? _b : null;
            if (footScanObject !== null) {
                marker.position.add(footScanObject.position);
            }
            marker.visible = true;
        };
        setInitialLandmarks(0 /* FootSide.Left */, false, (_b = (_a = this.leftScan) === null || _a === void 0 ? void 0 : _a.landmarks.mpj1) !== null && _b !== void 0 ? _b : null);
        setInitialLandmarks(0 /* FootSide.Left */, true, (_d = (_c = this.leftScan) === null || _c === void 0 ? void 0 : _c.landmarks.mpj5) !== null && _d !== void 0 ? _d : null);
        setInitialLandmarks(1 /* FootSide.Right */, false, (_f = (_e = this.rightScan) === null || _e === void 0 ? void 0 : _e.landmarks.mpj1) !== null && _f !== void 0 ? _f : null);
        setInitialLandmarks(1 /* FootSide.Right */, true, (_h = (_g = this.rightScan) === null || _g === void 0 ? void 0 : _g.landmarks.mpj5) !== null && _h !== void 0 ? _h : null);
        this.render();
        if (sendCallback) {
            (_j = this.landmarkChangeCallback) === null || _j === void 0 ? void 0 : _j.call(this, this.metadata);
        }
    }
    updateMetadata(side, landmarkInfo, templateInfo, sendCallback = true) {
        var _a, _b, _c, _d, _e;
        var _f, _g;
        if (landmarkInfo !== null || templateInfo !== null) {
            const metadata = ((_a = (_f = this.metadata)[_g = side === 0 /* FootSide.Left */ ? "left" : "right"]) !== null && _a !== void 0 ? _a : (_f[_g] = {
                version: GetCurrentMetadataVersion(),
            }));
            const footScanParameters = ((_b = metadata.footScanParameters) !== null && _b !== void 0 ? _b : (metadata.footScanParameters = {}));
            if (landmarkInfo !== null) {
                const landmarkPositions = ((_c = footScanParameters.landmarkPositions) !== null && _c !== void 0 ? _c : (footScanParameters.landmarkPositions = {}));
                landmarkPositions[landmarkInfo.isMPJ5 ? "mpj5" : "mpj1"] = DeepClone(landmarkInfo.position);
            }
            if (templateInfo !== null) {
                const dimensions = ((_d = metadata.dimensions) !== null && _d !== void 0 ? _d : (metadata.dimensions = {}));
                dimensions.length = templateInfo.length * mm2cm;
                dimensions.width = templateInfo.heelWidth * mm2cm;
                metadata.selectedTemplate = templateInfo.name;
            }
        }
        if (sendCallback) {
            (_e = this.landmarkChangeCallback) === null || _e === void 0 ? void 0 : _e.call(this, this.metadata);
        }
    }
    getMetadata() {
        return DeepClone(this.metadata);
    }
    setSize(width, height) {
        const aspect = width / height;
        const cameraViewportAspect = this.cameraViewportSize.x / this.cameraViewportSize.y;
        let aspectX;
        let aspectY;
        if (aspect > cameraViewportAspect) {
            // Window is wider than the foot scan bounding box
            aspectX = aspect / cameraViewportAspect;
            aspectY = 1;
        }
        else {
            // Window is taller than the foot scan bounding box
            aspectX = 1;
            aspectY = cameraViewportAspect / aspect;
        }
        this.camera.left = -this.cameraViewportSize.x * aspectX;
        this.camera.right = this.cameraViewportSize.x * aspectX;
        this.camera.top = this.cameraViewportSize.y * aspectY;
        this.camera.bottom = -this.cameraViewportSize.y * aspectY;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
        this.render(true);
    }
    getLandmarks() {
        var _a, _b, _c, _d;
        this.throwIfDisposed();
        function GetLandmarkOrNull(landmark) {
            if (landmark) {
                return DeepClone(landmark);
            }
            return null;
        }
        return {
            left: {
                mpj1: GetLandmarkOrNull((_a = this.leftScan) === null || _a === void 0 ? void 0 : _a.landmarks.mpj1),
                mpj5: GetLandmarkOrNull((_b = this.leftScan) === null || _b === void 0 ? void 0 : _b.landmarks.mpj5),
            },
            right: {
                mpj1: GetLandmarkOrNull((_c = this.rightScan) === null || _c === void 0 ? void 0 : _c.landmarks.mpj1),
                mpj5: GetLandmarkOrNull((_d = this.rightScan) === null || _d === void 0 ? void 0 : _d.landmarks.mpj5),
            },
        };
    }
    render(immediately = false) {
        if (immediately) {
            this.renderer.render(this.scene, this.camera);
            return;
        }
        if (this.renderRequested) {
            return;
        }
        this.renderRequested = true;
        window.requestAnimationFrame(() => {
            this.renderer.render(this.scene, this.camera);
            this.renderRequested = false;
        });
    }
    dispose() {
        this.throwIfDisposed();
        this.markerGeometry.dispose();
        this.markerTexture.dispose();
        this.markerMaterial.dispose();
        this.renderer.domElement.removeEventListener("pointerdown", this.raycastEventHandler);
        this.disposed = true;
    }
    raycastGlobal(worldRay) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const RaycastSide = (side) => __awaiter(this, void 0, void 0, function* () {
                const footScan = side === 0 /* FootSide.Left */ ? this.leftScan : this.rightScan;
                if (footScan === null) {
                    return null;
                }
                const engine = yield footScan.visualizer.acquireEngine(side);
                tmpMatrix4.copy(footScan.footScanObject.matrixWorld).invert();
                const localRay = new THREE.Ray();
                TransformRay(worldRay, tmpMatrix4, localRay);
                const tuv = yield engine.RaycasterRaycast(footScan.raycaster, Vector3ToFloat3(localRay.origin), Vector3ToFloat3(localRay.direction));
                footScan.visualizer.releaseEngine(side);
                if (tuv === null) {
                    return null;
                }
                const hitPoint = localRay.direction.clone().multiplyScalar(tuv.x).add(localRay.origin);
                return {
                    side,
                    point: hitPoint,
                    matrix: footScan.footScanObject.matrix,
                };
            });
            const [leftHit, rightHit] = yield Promise.all([RaycastSide(0 /* FootSide.Left */), RaycastSide(1 /* FootSide.Right */)]);
            // In this view, a ray hitting both foot scans is not really possible (since the view is from the bottom),
            // so just return the first hit
            return (_a = leftHit !== null && leftHit !== void 0 ? leftHit : rightHit) !== null && _a !== void 0 ? _a : null;
        });
    }
    throwIfDisposed() {
        if (this.disposed) {
            throw Error("Landmark selection scene was disposed");
        }
    }
}
//# sourceMappingURL=landmark-scene.js.map